import type { JSX, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { DevicePreferences } from "@centvox/shared/media";
import { bootstrap, meetingIdFromPath, takeInviteSecret } from "../../app/bootstrap.js";
import { navigate } from "../../app/navigate.js";
import { setIceServers } from "../../app/ice.js";
import { solveTurnstile } from "./turnstile.js";
import {
  centvoxFetch,
  ApiError,
  clearSessionDead,
  setAccessToken,
  setCsrfToken,
  setRefreshToken,
} from "../../api/fetcher.js";
import { loadPreferences, savePreferences } from "../../lib/prefs.js";
import { Button, Field, Spinner } from "../../ui/primitives.js";
import { StatusBand } from "../../ui/StatusBand.js";
import { PanelBoundary } from "../../ui/ErrorBoundary.js";
import { GreenRoom } from "./GreenRoom.js";
import { useI18n } from "../../i18n/react.js";
import { errorKey } from "../../i18n/errors.js";
import type { MessageKey } from "../../i18n/index.js";

/**
 * Katılım sayfası — **KRİTİK YOL** (Rapor 14 §3.2, Rapor 20 §6.3).
 *
 * ### ⚠️ Bütçe: ≤70 KB
 * ESLint (`no-restricted-imports`) burada `media/**` ve `routes/room/**`
 * **statik** import'unu yasaklar; `size-limit` bütçeyi CI'da ölçer ve aşım
 * **derlemeyi kırar**.
 *
 * ### ⭐ İzin isteminin "ölü zamanı"
 * `getUserMedia()` izin istemi kullanıcıyı saniyelerce bekletir. O sırada
 * **medya motoru paralel yüklenir** (dinamik import) — "Katıl" → ilk uzak kare
 * hedefi <3 sn (p75) böyle tutturulur (Rapor 14 §3.3, F1-33).
 *
 * ### ⚠️ Green room hata sınırıyla sarılır
 * Cihaz önizlemesi çökerse (bozuk sürücü, `AudioContext` reddi) kullanıcı yine
 * de **toplantıya katılabilmelidir**. Form ve önizleme ayrı ağaçlardır.
 */
export function JoinPage(): JSX.Element {
  const cfg = bootstrap();
  const i18n = useI18n();
  const [meetingId] = useState(() => meetingIdFromPath());
  const [secret] = useState(() => takeInviteSecret());
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [errorK, setErrorK] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<PublicMeeting | null>(null);
  const [prefs, setPrefs] = useState<DevicePreferences>(() => loadPreferences());
  /**
   * ⚠️ Turnstile kabı **formun içindedir**. `managed` modda Cloudflare şüpheli
   *    durumda etkileşimli bir kutucuk gösterir; `document.body`'ye eklenen
   *    çıplak bir div'de çizilseydi kutucuk formdan kopuk, muhtemelen ekran
   *    dışında belirir ve kullanıcı "Katıl"ın neden çalışmadığını anlamazdı.
   */
  const turnstileHost = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!meetingId) return;
    void centvoxFetch<PublicMeeting>(`/api/v1/meetings/${meetingId}/public`)
      .then(setInfo)
      .catch((e: unknown) => setErrorK(e instanceof ApiError ? errorKey(e.code) : "join.notFound"));
  }, [meetingId]);

  const updatePrefs = useCallback((next: DevicePreferences) => {
    setPrefs(next);
    savePreferences(next);
  }, []);

  /**
   * ⭐ Medya motoru **kullanıcı "Katıl"a basar basmaz**, ağ isteğiyle PARALEL
   * yüklenir. `await` edilmez; sonuç katılım yanıtıyla buluşturulur.
   */
  const preloadMedia = useCallback(() => import("../../media/engine.js"), []);

  const onJoin = useCallback(async () => {
    if (!meetingId || !secret) {
      setErrorK("join.missingSecret");
      return;
    }
    setBusy(true);
    setErrorK(null);

    const mediaPromise = preloadMedia();

    try {
      const host = turnstileHost.current;
      if (host === null) throw new Error("turnstile_host_missing");
      const check = await solveTurnstile(cfg.turnstileSiteKey, host);
      if (!check.ok) {
        /**
         * ⚠️ Üç neden AYRI mesaj alır. Hepsini "doğrulama başarısız" diye
         *    göstermek, yapılandırma eksikliğini (yönetici işi) kullanıcı
         *    hatası gibi gösterirdi.
         */
        setErrorK(
          check.reason === "unconfigured"
            ? "join.turnstileUnconfigured"
            : check.reason === "unreachable"
              ? "join.turnstileUnreachable"
              : "join.turnstileFailed",
        );
        setBusy(false);
        return;
      }
      const turnstileToken = check.token;
      const res = await centvoxFetch<JoinResponse | JoinWaiting>(
        `/api/v1/meetings/${meetingId}/join`,
        {
          method: "POST",
          idempotencyKey: `join-${meetingId}-${crypto.randomUUID()}`,
          body: JSON.stringify({
            name: name.trim(),
            // ⚠️ Sır GÖVDEDE gider, URL'de değil.
            secret,
            ...(pin ? { pin } : {}),
            turnstileToken,
            deviceHints: { hasCam: prefs.cam, hasMic: prefs.mic },
          }),
        },
      );

      // Access token **yalnız bellekte**; `localStorage`'a yazılmaz.
      setAccessToken(res.accessToken);
      /**
       * 🚨 **CSRF jetonu gövdeden alınır.** Çerez API alan adına kurulur ve
       *    Pages'teki JS onu okuyamaz; okunamadığı için `/token/refresh`
       *    çift gönderim kontrolünde her seferinde düşüyordu. Yani erişim
       *    jetonu (5 dk) dolduğu anda oturum kurtarılamaz hâle geliyordu.
       */
      setCsrfToken(res.csrfToken ?? null);
      // ⚠️ Önceki oturumdan kalan "kurtarılamaz" bayrağı temizlenir; yoksa yeni
      //    katılımda soket ilk denemede kendini kapatırdı.
      clearSessionDead();
      // ⚠️ Çerez engellenirse yenileme YALNIZ bu jetonla mümkün (bkz. fetcher).
      setRefreshToken(res.refreshToken ?? null);
      /**
       * 🚨 **ICE sunucuları BURADA yakalanır.** Önceden bu alan okunmuyordu ve
       *    tüm PeerConnection'lar TURN'süz kuruluyordu — simetrik NAT
       *    arkasındaki hiç kimse bağlanamıyordu (bkz. `app/ice.ts`).
       *    Lobi yanıtında (202) da gelir; kabul edildiğinde hazır olur.
       */
      setIceServers(res.iceServers);
      savePreferences(prefs);
      await mediaPromise;

      /**
       * 🚨 **`location.assign` DEĞİL** — tam sayfa yüklemesi, bellekte tutulan
       *    erişim jetonunu siliyordu ve oda jetonsuz açılıyordu. Kurtarma yolu
       *    olan yenileme çerezi de çapraz sitede hiç gönderilmiyor; yani
       *    katılım akışı uçtan uca tamamlanamıyordu (bkz. `app/navigate.ts`).
       */
      /**
       * ⭐ **Lobi bilgisi URL bayrağıyla taşınır** (`?lobby=1`).
       *
       * ⚠️ Bayrak zaten üretiliyordu ama **hiçbir yerde okunmuyordu**; oda,
       *    lobide olduğunu ancak WebSocket snapshot'ı gelince öğreniyordu.
       *    Snapshot gecikirse (ya da bağlantı iptal olursa) lobideki kullanıcı
       *    yayına kalkıyor ve `capability_denied` alıyordu. Artık denetleyici
       *    başlarken bayrağı okuyor (`routes/room/controller.ts`).
       *
       * ⚠️ Store'a doğrudan yazmak DAHA KOLAY olurdu ama `signaling/store`'u
       *    buraya import etmek onu **kritik yola** sokuyor: ölçüldü, paket
       *    72 KB'den 90 KB'ye çıktı. Katılım sayfasının bütçesi bu değişikliğe
       *    ait değil (Rapor 14 §3.2).
       */
      const waiting = "state" in res && res.state === "waiting";
      navigate(waiting ? `/room/${meetingId}?lobby=1` : `/room/${meetingId}`);
    } catch (e) {
      setErrorK(e instanceof ApiError ? errorKey(e.code) : "join.failed");
    } finally {
      setBusy(false);
    }
  }, [cfg.turnstileSiteKey, meetingId, name, pin, preloadMedia, prefs, secret]);

  if (!meetingId) {
    /**
     * ⚠️ Buraya **yalnız `/j/…` altındaki bozuk bir bağlantı** düşer.
     *
     * Kök yol (`/`) artık bu bileşene hiç gelmez: `app/route.ts` onu tanıtım
     * sayfasına yönlendirir. Önceden gelirdi ve davet bağlantısı olmayan her
     * ziyaretçiye kırmızı "Katılım bağlantısı geçersiz" bandı gösterilirdi —
     * kullanıcı yanlış bir şey yapmamışken ürün bozuk görünürdü.
     *
     * Burada kalan durum gerçek bir hatadır: adres `/j/` ile başlıyor ama
     * toplantı kimliği 32 haneli onaltılık bir değer değil. Kullanıcının
     * bağlantısı kırpılmış veya bozulmuştur ve bunu bilmesi gerekir.
     */
    return (
      <Shell title={cfg.productName}>
        <StatusBand candidates={[{ level: "error", message: i18n.t("join.invalidLink") }]} />
        <p data-hint>{i18n.t("landing.hostHint")}</p>
      </Shell>
    );
  }

  return (
    <Shell title={info?.title ?? cfg.productName}>
      {/* ⚠️ Önizleme çökerse katılım formu AYAKTA kalır. */}
      <PanelBoundary name="green-room">
        <GreenRoom prefs={prefs} onChange={updatePrefs} />
      </PanelBoundary>

      {/*
        ⚠️ Form, önizlemeyle AYNI sütuna oturur (`data-join-column`). Önceden
           kendi genişliğindeydi ve ekranda üçüncü bir sol kenar açıyordu;
           göz bir sayfayı sol kenarlardan okur ve üç ayrı kenar üç ayrı
           sayfa gibi hissettirir.
      */}
      <form
        data-join-column
        onSubmit={(e) => {
          e.preventDefault();
          void onJoin();
        }}
      >
        <Field
          label={i18n.t("join.nameLabel")}
          placeholder={i18n.t("join.namePlaceholder")}
          value={name}
          onChange={setName}
          maxLength={64}
          required
          autoComplete="name"
          autoFocus
        />

        {info?.pinRequired ? (
          <Field
            label={i18n.t("join.pinLabel")}
            value={pin}
            onChange={setPin}
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
          />
        ) : null}

        {/*
          ⚠️ Kap her zaman DOM'dadır ama `managed` modda çoğu kullanıcıda boş
          kalır: Cloudflare yalnız gerektiğinde kutucuk çizer.
        */}
        <div ref={turnstileHost} data-turnstile />

        {/* Tek slotlu, öncelikli durum bandı (Rapor 14 §9) */}
        <StatusBand
          candidates={[
            errorK !== null && { level: "error" as const, message: i18n.t(errorK) },
            info?.lobbyEnabled === true && {
              level: "info" as const,
              message: i18n.t("join.lobbyNotice"),
            },
          ]}
        />

        <Button type="submit" variant="primary" busy={busy} disabled={name.trim().length === 0}>
          {busy ? (
            <>
              <Spinner /> {i18n.t("join.submitting")}
            </>
          ) : (
            i18n.t("join.submit")
          )}
        </Button>
      </form>

      {/*
        ⚠️ VAAT DİLİ BAĞLAYICIDIR (Rapor 08 §9):
        "Uçtan uca şifreli" ifadesi KULLANILAMAZ — E2EE v2 opsiyonel moddur.
      */}
      <footer>
        <small>{i18n.t("join.encryptionNotice")}</small>
      </footer>
    </Shell>
  );
}

function Shell(props: { title: string; children: ReactNode }): JSX.Element {
  const cfg = bootstrap();
  return (
    <main id="main">
      {/*
        ⚠️ Logo **kiracı markasıdır** ve `alt` metni ürün adıdır: görsel
        yüklenmezse kullanıcı yine hangi ürüne baktığını bilir. Boş `alt`
        vermek, ekran okuyucuda başlıksız bir sayfa üretirdi.
        ⚠️ URL Worker tarafında **HTTPS olarak doğrulanmıştır** (`safeLogoUrl`);
        burada ikinci bir doğrulama yapılmaz — tek kapı sunucudadır.
      */}
      {cfg.logoUrl ? (
        <img src={cfg.logoUrl} alt={cfg.productName} data-brand-logo height={40} />
      ) : null}
      <h1>{props.title}</h1>
      {props.children}
    </main>
  );
}

interface PublicMeeting {
  id: string;
  title: string;
  status: string;
  lobbyEnabled: boolean;
  pinRequired: boolean;
}

interface JoinResponse {
  pid: string;
  accessToken: string;
  wsUrl: string;
  /** ⚠️ Çerez çapraz kökende okunamaz; jeton BURADAN alınır. */
  csrfToken?: string;
  refreshToken?: string;
  /** ⚠️ Alan sözleşmede VARDI ama okunmuyordu; TURN'süz bağlantı üretiyordu. */
  iceServers?: unknown;
}

interface JoinWaiting {
  state: "waiting";
  accessToken: string;
  wsUrl: string;
  csrfToken?: string;
  refreshToken?: string;
  iceServers?: unknown;
}
